/**
 * IndustryVersionTimeline — shared version-history view for an industry's
 * Industry Research record. Extracted from IndustryDetail's "Versions" tab
 * so the same UI can render inside CaseDetail's "Industry Versions" tab
 * without duplicating the layout / styling.
 *
 * Fetches the timeline directly via `getIndustryTimeline` so the component
 * is self-contained — callers just pass the `industryId`. The TanStack
 * cache key is the standard one used elsewhere in the app, so opening this
 * tab after browsing IndustryDetail is instant.
 *
 * Returns null when industryId is missing so callers can mount unconditionally.
 */
import { useQuery } from '@tanstack/react-query';
import { Calendar, AlertCircle } from 'lucide-react';
import {
  getIndustryTimeline,
  type IndustryTimeline,
} from '../../lib/api/industryResearchApi';

interface IndustryVersionTimelineProps {
  industryId: string | null | undefined;
}

const STALE_30_MIN = 1000 * 60 * 30;

export default function IndustryVersionTimeline({
  industryId,
}: IndustryVersionTimelineProps) {
  const { data, isLoading, error } = useQuery<IndustryTimeline>({
    queryKey: ['industry', 'timeline', industryId],
    queryFn: () => getIndustryTimeline(industryId!),
    enabled: !!industryId,
    staleTime: STALE_30_MIN,
    retry: false,
  });

  if (!industryId) return null;

  if (isLoading) {
    // Skeleton mirrors the loaded layout: total-versions summary line plus
    // three left-bordered timeline cards. Matches the final shape so the
    // page doesn't shift when real versions resolve.
    return (
      <div className="bg-white border border-neutral-200 rounded-lg p-6 animate-pulse">
        <div className="h-5 w-40 bg-neutral-200 rounded mb-4" />
        <div className="h-3 w-32 bg-neutral-100 rounded mb-6" />
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="border-l-4 border-neutral-200 pl-4 pb-4">
              <div className="flex items-start justify-between mb-2">
                <div className="space-y-1.5">
                  <div className="h-4 w-20 bg-neutral-200 rounded" />
                  <div className="h-2.5 w-44 bg-neutral-100 rounded" />
                </div>
                <div className="h-6 w-16 bg-neutral-100 rounded-full" />
              </div>
              <div className="h-3 w-3/4 bg-neutral-100 rounded mt-2" />
              <div className="h-3 w-32 bg-neutral-100 rounded mt-2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <p className="text-sm">
          Could not load version history.{' '}
          <span className="text-xs">{(error as Error).message}</span>
        </p>
      </div>
    );
  }

  if (!data || data.timeline.length === 0) {
    return (
      <div className="bg-white border border-neutral-200 rounded-lg p-6 text-center">
        <Calendar className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
        <p className="text-neutral-600 text-sm">
          No version history yet — only the initial research record exists.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white border border-neutral-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-neutral-900 mb-4">
          Version Timeline
        </h2>
        <p className="text-neutral-600 mb-4">
          Total versions: <span className="font-semibold">{data.totalVersions}</span>
        </p>

        <div className="space-y-4">
          {data.timeline.map((version) => (
            <div
              key={version.versionId}
              className="border-l-4 border-primary-600 pl-4 pb-4"
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900">
                    {version.semanticVersion}
                  </h3>
                  <p className="text-sm text-neutral-600">
                    {version.reportPeriod} •{' '}
                    {new Date(version.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    version.changeType === 'major'
                      ? 'bg-red-100 text-red-800'
                      : version.changeType === 'minor'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  {version.changeType}
                </span>
              </div>
              {version.changeDescription && (
                <p className="text-sm text-neutral-700 mt-2">
                  {version.changeDescription}
                </p>
              )}
              <div className="mt-2 text-sm text-neutral-600">
                Materiality Score:{' '}
                <span className="font-medium">
                  {(version.materialityScore * 100).toFixed(0)}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
